import {useNavigate} from 'react-router-dom';

import {APIError} from 'data/errors';
import {HttpStatusCode} from 'data/protocols/http';
import {TypeLocalNameEnum} from 'domain/usecases';
import {
	OperationStateTypesEnum,
	QueryParamsEnum,
	QueryParamsForTaggingEnum,
} from 'presentation/constants';
import {usePersonalData} from 'presentation/hooks';
import {
	FlowEnum,
	REBOOKING_PATH,
	TELEMEDICINE_INFO,
} from 'presentation/pages/private/booking/constants';
import {
	WellBornEndpoints,
	gynecologyNames,
} from 'presentation/pages/private/booking/constants/beBornWell';

import {GetHealthDistrictsParams} from 'domain/usecases/private/booking-flow/health/get-health-districts';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useUnitsListStore} from 'presentation/modules/booking/shared/store/useUnitsListStore';
import {getStepPosition} from 'presentation/modules/booking/shared/utils/getStepPosition';
import {useQueryParams} from 'presentation/pages/private/hooks';
import {getUserInfoFromToken} from 'presentation/utils';
import {useLocationStore} from 'presentation/pages/private/store/locationStore';
import {UseMyAppointmentsWellBornParams, UseMyAppointmentsWellBornReturn} from './types';
import {useGetHealthDistrictsQuery} from 'presentation/modules/booking/health/ui/pages/HealthBooking/steps/HealthDistrictSelection/queries/useGetHealthDistrictsQuery';
import {getWellBornEligibility} from 'presentation/modules/booking/shared/services/getWellBornEligibility';

export const useMyAppointmentsWellBorn = ({
	specialtyName,
	specialtyCode,
	subSpecialtyCode,
	bookingNumber,
	bookingType,
	ufCode,
	cityCode,
	ufName,
	email,
	phone,
	flowType,
	isTelehealth,
	currentAppointmentLocation,
	locationType,
	setOperationState,
	setIsBornWellActiveState,
	getBookingTypeAndOrigin,
	setActiveBookingFlowStep,
}: UseMyAppointmentsWellBornParams): UseMyAppointmentsWellBornReturn => {
	const navigate = useNavigate();
	const setBookingData = useBookingStore(state => state.setBookingData);
	const {setSearchParams} = useQueryParams();
	const resetUnitResponseState = useUnitsListStore(state => state.resetUnitsList);
	const {currentEmail, currentPhone} = usePersonalData();
	const cdPessoaC = getUserInfoFromToken().cdPessoa;
	const setLocation = useLocationStore(state => state.setLocation);

	const isHealth = flowType === FlowEnum.HEALTH;

	const healthDistrictParams: GetHealthDistrictsParams = {
		stateCode: ufCode?.toUpperCase(),
		locationName: cityCode?.toUpperCase(),
		locationType: TypeLocalNameEnum.CIDADE,
		specialtyCode: Number(specialtyCode),
	};

	const {refetch: refetchHealthDistricts} = useGetHealthDistrictsQuery({
		params: healthDistrictParams,
		enabled: false,
	});

	const {SPECIALITY_OR_EXAM_STEP, UNIT_STEP, DISTRICT_STEP, PROFESSIONAL_STEP} = getStepPosition({
		flowType,
		isManualLocation: true,
	});

	const someCurrentAppointmentLocationIsEmpty =
		!currentAppointmentLocation.city || !currentAppointmentLocation.state?.cdUf;

	const getRebookingRedirectStep = (areDistrictsEmpty: boolean = false) => {
		if (areDistrictsEmpty && isHealth) {
			return UNIT_STEP;
		}

		if (isTelehealth) {
			return PROFESSIONAL_STEP;
		}

		return DISTRICT_STEP;
	};

	const handleWellBornEligibility = async (): Promise<boolean> => {
		let areDistrictsEmpty = false;
		if (isHealth) {
			const {
				data: districtList,
				error: districtListError,
				isError: isDistrictListError,
			} = await refetchHealthDistricts();

			const districtListHasUnprocessableError =
				isDistrictListError &&
				districtListError instanceof APIError &&
				districtListError.statusCode === HttpStatusCode.unprocessable;
			const districtListEmpty = districtList && districtList?.length === 0;
			areDistrictsEmpty = Boolean(districtListHasUnprocessableError || districtListEmpty);
		}

		const specialtyNameUpper = specialtyName.toUpperCase();

		const bookingOrigin = getBookingTypeAndOrigin();

		const commonBookingData = {
			specialtyCode,
			subSpecialtyCode: !subSpecialtyCode || subSpecialtyCode === '0' ? '' : subSpecialtyCode,
			specialtyName,
			bookingNumber,
			isRebooking: true,
			cityName: cityCode.toUpperCase(),
			ufCode,
			ufName: ufName.toUpperCase(),
			email: email || currentEmail,
			phone: phone || currentPhone,
			dateAt: '',
			hourAt: '',
			professionalCode: '',
			professionalName: '',
			unitAddress: '',
			unitCode: '',
			unitName: '',
			districtName: '',
			typeLocalName: TypeLocalNameEnum.CIDADE,
			bookingType,
			bookingOrigin,
			isScheduleScript: false,
			isManualLocation: true,
			allDistrictsSelected: false,
		};

		if (gynecologyNames.some(name => name === specialtyNameUpper)) {
			try {
				const eligibilityResponse = await getWellBornEligibility({
					stateCode: ufCode,
					typeLocalName: TypeLocalNameEnum.CIDADE,
					localName: cityCode.toUpperCase(),
					specialtyCode,
				});

				const isNegativeInProgram =
					eligibilityResponse?.usuarioNegativaPrograma &&
					eligibilityResponse?.especialidadePrograma &&
					eligibilityResponse?.cidadePrograma === WellBornEndpoints.WELL_BORN;

				const isCityInProgramButSpecialtyNot =
					(eligibilityResponse?.usuarioEmCaptacao === false ||
						eligibilityResponse?.usuarioNoPrograma === false) &&
					eligibilityResponse?.especialidadePrograma === false &&
					eligibilityResponse?.cidadePrograma === WellBornEndpoints.WELL_BORN;

				const isActiveInProgram =
					(eligibilityResponse?.usuarioEmCaptacao ||
						eligibilityResponse?.usuarioNoPrograma) &&
					eligibilityResponse?.cidadePrograma === WellBornEndpoints.WELL_BORN;

				if (isNegativeInProgram) {
					resetUnitResponseState();

					setBookingData(prev => ({
						...prev,
						...commonBookingData,
						allDistrictsSelected: false,
						citiesNames: '',
						coordinates: '',
						hasGeoDistrictsFilter: false,
					}));

					setSearchParams(prev => {
						prev.delete(QueryParamsEnum.SEARCH_QUERY);
						return prev;
					});

					const baseParams: Record<string, string> = {
						[QueryParamsForTaggingEnum.STEP]: '1',
						[QueryParamsForTaggingEnum.SPECIALTY_CODE]: specialtyCode,
						[QueryParamsForTaggingEnum.SUB_SPECIALTY_CODE]:
							!subSpecialtyCode || subSpecialtyCode === '0' ? '' : subSpecialtyCode,
						[QueryParamsForTaggingEnum.SPECIALTY_NAME]: specialtyName,
						[QueryParamsForTaggingEnum.BOOKING_NUMBER]: bookingNumber,
						[QueryParamsForTaggingEnum.IS_REBOOKING]: 'true',
						[QueryParamsForTaggingEnum.CITY_NAME]: cityCode.trim().toUpperCase(),
						[QueryParamsForTaggingEnum.UF_CODE]: ufCode,
						[QueryParamsForTaggingEnum.UF_NAME]: ufName.trim().toUpperCase(),
						[QueryParamsForTaggingEnum.SHOW_BE_BORN_WELL_NOT_REGISTER]: 'true',
					};

					if (isTelehealth) {
						setBookingData(prev => ({
							...prev,
							unitCode: TELEMEDICINE_INFO.code,
							unitName: TELEMEDICINE_INFO.name,
						}));

						baseParams[QueryParamsForTaggingEnum.UNIT_CODE] = TELEMEDICINE_INFO.code;
						baseParams[QueryParamsForTaggingEnum.UNIT_NAME] = TELEMEDICINE_INFO.name;
					}

					const taggingParams = Object.entries(baseParams)
						.map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
						.join('&');

					const path = REBOOKING_PATH[flowType as keyof typeof REBOOKING_PATH];
					const url = `${path}?${taggingParams}`;

					if (!isTelehealth && someCurrentAppointmentLocationIsEmpty) {
						setLocation(cdPessoaC, locationType, {
							city: cityCode.toUpperCase(),
							state: {
								cdUf: ufCode,
								nmUf: ufName,
							},
							typeLocalName: TypeLocalNameEnum.CIDADE,
						});
					}

					setOperationState?.(OperationStateTypesEnum.DONE);
					setActiveBookingFlowStep(SPECIALITY_OR_EXAM_STEP);
					navigate(url, {replace: true});
					return true;
				}
				if (isCityInProgramButSpecialtyNot) {
					resetUnitResponseState();

					setBookingData(prev => ({
						...prev,
						...commonBookingData,
					}));

					setSearchParams(prev => {
						prev.delete(QueryParamsEnum.SEARCH_QUERY);
						return prev;
					});

					const baseParams: Record<string, string> = {
						[QueryParamsForTaggingEnum.STEP]: '1',
						[QueryParamsForTaggingEnum.SPECIALTY_CODE]: specialtyCode,
						[QueryParamsForTaggingEnum.SUB_SPECIALTY_CODE]:
							!subSpecialtyCode || subSpecialtyCode === '0' ? '' : subSpecialtyCode,
						[QueryParamsForTaggingEnum.SPECIALTY_NAME]: specialtyName,
						[QueryParamsForTaggingEnum.BOOKING_NUMBER]: bookingNumber,
						[QueryParamsForTaggingEnum.IS_REBOOKING]: 'true',
						[QueryParamsForTaggingEnum.CITY_NAME]: cityCode.trim().toUpperCase(),
						[QueryParamsForTaggingEnum.UF_CODE]: ufCode,
						[QueryParamsForTaggingEnum.UF_NAME]: ufName.trim().toUpperCase(),
					};

					if (isTelehealth) {
						setBookingData(prev => ({
							...prev,
							unitCode: TELEMEDICINE_INFO.code,
							unitName: TELEMEDICINE_INFO.name,
						}));

						baseParams[QueryParamsForTaggingEnum.UNIT_CODE] = TELEMEDICINE_INFO.code;
						baseParams[QueryParamsForTaggingEnum.UNIT_NAME] = TELEMEDICINE_INFO.name;
					}

					const taggingParams = Object.entries(baseParams)
						.map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
						.join('&');

					const path = REBOOKING_PATH[flowType as keyof typeof REBOOKING_PATH];
					const url = `${path}?${taggingParams}`;

					if (!isTelehealth && someCurrentAppointmentLocationIsEmpty) {
						setLocation(cdPessoaC, locationType, {
							city: cityCode.toUpperCase(),
							state: {
								cdUf: ufCode,
								nmUf: ufName,
							},
							typeLocalName: TypeLocalNameEnum.CIDADE,
						});
					}

					setOperationState?.(OperationStateTypesEnum.DONE);
					setActiveBookingFlowStep(getRebookingRedirectStep(areDistrictsEmpty));
					navigate(url, {replace: true});
					return true;
				}
				if (isActiveInProgram) {
					setIsBornWellActiveState(true);
					setOperationState?.(OperationStateTypesEnum.DONE);
					return true;
				}
			} catch {
				setOperationState?.(OperationStateTypesEnum.ERROR);
				return true;
			}
		}

		return false;
	};

	return {
		handleWellBornEligibility,
	};
};
